import 'leaflet/dist/leaflet.css';

import { Banner } from '@/components/banner/banner.component';
import { SoilPyramid } from '@/components/charts/key-soil-pyramid-chart.component';
import { MultiBarScatterChart } from '@/components/charts/multi-bar-bubble-chart.component';
import { OrganicMatterChart } from '@/components/charts/organic-matter-chart.component';
import { AdminContainer } from '@/components/containers';
import { Loading } from '@/components/loading';
import { FarmMap } from '@/components/maps';
import { FieldNavigator } from '@/components/navigation/field-navigator.component';
import { FieldNutrientSection } from '@/components/sections/field-nutrient-section.component';
import { CarbonStockKeyTable, FarmMapTable } from '@/components/tables/farm-map-tables.component';
import { FieldCarbonTable } from '@/components/tables/field-carbon-table.component';
import { FieldDensityTable } from '@/components/tables/field-density-table.component';
import { FieldToggle, ReportTabs, ToggleTabsWithoutReport } from '@/components/toggle/toggle.component';
import { H3 } from '@/components/typography';
import { cn } from '@/lib/utils';
import { useFieldMap } from '@/modules/Farms/Farms.api';
import { useFieldCarbonStockData, useFieldData, useOrganicMatterData } from '@/modules/Report/Report.api';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

function FieldDetails() {
  const { farmId, fieldId } = useParams<{
    customerId: string;
    projectId: string;
    farmId: string;
    fieldId: string;
  }>();
  const { data: fieldMapData, status: fieldMapStatus } = useFieldMap(fieldId);

  const [visibleTab, setVisibleTab] = useState<ToggleTabsWithoutReport>(ReportTabs.LIST);

  const handleTabChange = (tab: ToggleTabsWithoutReport) => {
    setVisibleTab(tab);
  };

  const { data: fieldData, status: fieldDataStatus } = useFieldData(fieldId);
  const { data: bannerData, status: bannerDataStatus } = useFieldCarbonStockData(fieldId);
  const { data: organicMatterData } = useOrganicMatterData(fieldId!);

  const mapView = useMemo(() => {
    if (!fieldMapData) {
      return;
    }
    return (
      <div className={cn('flex-col mb-10')}>
        <H3 className="mb-5">Field Map</H3>
        <CarbonStockKeyTable />
        <div className={cn('flex flex-grow relative w-full aspect-square max-h-[615px]')}>
          <FarmMap zoom={13} data={fieldMapData} />
        </div>
        <FarmMapTable data={fieldMapData} fieldId={fieldId} className="mt-8" />
      </div>
    );
  }, [fieldMapData, fieldId]);

  const fieldCarbonTable = useMemo(() => {
    if (fieldDataStatus === 'pending') {
      return <Loading />;
    }

    if (fieldDataStatus === 'error') {
      return;
    }
    return <FieldCarbonTable fieldData={fieldData} className={cn('p-5 shadow')} />;
  }, [fieldData, fieldDataStatus]);

  const carbonStockBanner = useMemo(() => {
    if (bannerDataStatus === 'pending') {
      return <Loading />;
    }

    if (bannerDataStatus === 'error') {
      return;
    }

    return (
      bannerData[0].cstock_comment && (
        <Banner text={<>The soil carbon stock in this field is {bannerData[0].cstock_comment} compared to the rest of your farm.</>} />
      )
    );
  }, [bannerData, bannerDataStatus]);

  const listView = useMemo(() => {
    if (!farmId || !fieldId) {
      return;
    }
    return (
      <div className={cn('flex flex-col gap-4')}>
        {fieldCarbonTable}
        {carbonStockBanner}
        <FieldDensityTable fieldId={fieldId} />
        <div className={cn('shadow p-7 mb-6')}>
          <MultiBarScatterChart farmId={farmId} fieldId={fieldId} />
        </div>
        <SoilPyramid fieldId={fieldId} />
        {organicMatterData?.chart && (
          <div className={cn('shadow p-7 mb-6')}>
            <OrganicMatterChart fieldId={fieldId} />
          </div>
        )}
        <FieldNutrientSection farmId={farmId} fieldId={fieldId} />
      </div>
    );
  }, [fieldCarbonTable, carbonStockBanner, farmId, fieldId, organicMatterData]);

  const fieldDetailsContent = useMemo(() => {
    if (fieldMapStatus === 'pending') {
      return <Loading />;
    }
    return visibleTab === ReportTabs.LIST ? listView : mapView;
  }, [fieldMapStatus, visibleTab, listView, mapView]);

  if (fieldDataStatus === 'error') {
    console.error('Error fetching farm information:');
    toast.error('Failed to fetch farm information');
    throw new Error('Failed to fetch farm information');
  }

  if (fieldMapStatus === 'error') {
    console.error('Error fetching GeoJSON:');
    toast.error('Failed to retrieve map data. Please try again.');
    throw new Error('Failed to fetch field map data');
  }

  const toggle = <FieldToggle visibleTab={visibleTab} setVisibleTab={handleTabChange} />;

  return (
    <AdminContainer
      className={cn('flex flex-col')}
      title={
        <div className="flex items-center justify-between w-full h-full">
          {fieldData ? fieldData.fieldInfo.generalInfo.fieldName : 'Field Description'}
          <div className="h-full flex items-center">
            <FieldNavigator farmId={farmId!} currentFieldId={fieldId!} />
          </div>
        </div>
      }
      breadcrumbData={fieldData?.breadcrumb}
      searchAndFilter={toggle}
    >
      <div className="mt-4">{fieldDetailsContent}</div>
    </AdminContainer>
  );
}

export default FieldDetails;
